import { Button, Checkbox, FormControlLabel, IconButton, TextField, Tooltip } from "@mui/material";
import { AutoAwesome } from "@mui/icons-material";
import clsx from "clsx";
import { Formik } from "formik";
import _ from "lodash";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { t } from "ttag";
import * as Yup from "yup";
import { useStoreActions } from "../../models/RootStore";
import SimpleModal from "../../widgets/SimpleModal/SimpleModal";
import { MockDeviceStateField } from "./MockDeviceStateSwitches";
import { SIMULATOR_SERIAL_REGEX, generateSimulatorSerial } from "./simulatorSerial";
import useStyles from "./AddEditMockDevice.style";

const SERIAL_CHECK_DEBOUNCE_MS = 400;

const generateRandomPin = () => String(Math.floor(1000 + Math.random() * 9000));

const DeviceSchema = Yup.object().shape({
  name: Yup.string().required(t`Required`),
  simulatorConnected: Yup.boolean(),
  serial: Yup.string().when("simulatorConnected", {
    is: true,
    then: (schema) => schema.matches(SIMULATOR_SERIAL_REGEX, t`Must match the simulator's serial format`).required(t`Required`),
    otherwise: (schema) => schema.required(t`Required`),
  }),
  pin: Yup.string().when("simulatorConnected", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required(t`Required`),
  }),
});

const STATE_FIELDS: { field: MockDeviceStateField; label: string }[] = [
  { field: "connected", label: t`Connected` },
  { field: "registered", label: t`Registered` },
  { field: "blocked", label: t`Blocked` },
  { field: "proLicense", label: t`Pro License` },
];

const AddEditMockDevice = ({ isEdit, device, onClose, onSave, onRequestDelete }: any) => {
  const styles: any = useStyles();
  const checkMockSerialAvailable = useStoreActions((actions) => actions.devices.checkMockSerialAvailable);
  const generateMockSerial = useStoreActions((actions) => actions.devices.generateMockSerial);
  const setMockDeviceConnection = useStoreActions((actions) => actions.devices.setMockDeviceConnection);
  const setMockDeviceRegistration = useStoreActions((actions) => actions.devices.setMockDeviceRegistration);
  const setMockDeviceBlocked = useStoreActions((actions) => actions.devices.setMockDeviceBlocked);
  const setMockDeviceProLicense = useStoreActions((actions) => actions.devices.setMockDeviceProLicense);

  const [serialError, setSerialError] = useState<string>("");
  const [checkingSerial, setCheckingSerial] = useState<boolean>(false);
  const [generatingSerial, setGeneratingSerial] = useState<boolean>(false);
  const lastRequestId = useRef(0);

  const runSerialCheck = useCallback(async (serial: string) => {
    if (!serial) {
      setSerialError("");
      return;
    }

    const requestId = ++lastRequestId.current;
    setCheckingSerial(true);

    const result: any = await checkMockSerialAvailable(serial).catch(() => null);

    if (requestId !== lastRequestId.current) {
      return;
    }

    setCheckingSerial(false);
    setSerialError(result && result.available === false ? t`Serial already exists` : "");
  }, [checkMockSerialAvailable]);

  const debouncedSerialCheck = useMemo(
    () => _.debounce(runSerialCheck, SERIAL_CHECK_DEBOUNCE_MS),
    [runSerialCheck]
  );

  const handleSubmit = (values: any) => {
    onSave(values);
  };

  const dispatchStateField = (field: MockDeviceStateField, nextValue: boolean) => {
    if (field === "connected") {
      setMockDeviceConnection({ id: device.id, connected: nextValue });
    } else if (field === "registered") {
      setMockDeviceRegistration({ id: device.id, registered: nextValue });
    } else if (field === "blocked") {
      setMockDeviceBlocked({ id: device.id, blocked: nextValue });
    } else {
      setMockDeviceProLicense({ id: device.id, proLicense: nextValue });
    }
  };

  return (
    <SimpleModal showModal={true} title={isEdit ? t`Edit Virtual Device` : t`Create Virtual Device`} hideBtns>
      <Formik
        initialValues={{
          name: isEdit && device ? device.name : "",
          serial: isEdit && device ? device.serial : "",
          pin: isEdit && device ? device.pin : "",
          simulatorConnected: isEdit && device ? !!device.simulatorConnected : false,
          connected: isEdit && device ? !!device.connected : false,
          registered: isEdit && device ? !!device.registered : false,
          blocked: isEdit && device ? !!device.blocked : false,
          proLicense: isEdit && device ? !!device.proLicense : false,
        }}
        enableReinitialize={true}
        validateOnChange={false}
        onSubmit={handleSubmit}
        validationSchema={DeviceSchema}
      >
        {(props: any) => {
          const { values, errors, handleChange, handleSubmit: formikSubmit, setFieldValue } = props;

          const handleSerialChange = (event: any) => {
            handleChange(event);
            debouncedSerialCheck(event.target.value);
          };

          const handleSpark = async () => {
            if (values.simulatorConnected) {
              const serial = generateSimulatorSerial();
              setFieldValue("serial", serial);
              runSerialCheck(serial);
              return;
            }

            setGeneratingSerial(true);
            const result: any = await generateMockSerial().catch(() => null);
            setGeneratingSerial(false);

            if (result && result.serial) {
              setFieldValue("serial", result.serial);
              runSerialCheck(result.serial);
            }

            setFieldValue("pin", generateRandomPin());
          };

          const handleSimulatorConnectedChange = (event: any) => {
            setFieldValue("simulatorConnected", event.target.checked);
            setFieldValue("serial", "");
            setFieldValue("pin", "");
            setSerialError("");
          };

          const handleStateFieldToggle = (field: MockDeviceStateField) => () => {
            const nextValue = !values[field];
            setFieldValue(field, nextValue);

            if (isEdit && device) {
              dispatchStateField(field, nextValue);
            }
          };

          const serialFormatInvalid = values.simulatorConnected
            && !!values.serial
            && !SIMULATOR_SERIAL_REGEX.test(values.serial);

          const saveDisabled = isEdit
            ? false
            : !!serialError
              || checkingSerial
              || !values.serial
              || serialFormatInvalid
              || (!values.simulatorConnected && !values.pin);

          const renderStateCheckboxes = () => (
            <div className={styles.stateCheckboxes}>
              {STATE_FIELDS.map(({ field, label }) => (
                <FormControlLabel
                  key={field}
                  control={<Checkbox checked={!!values[field]} onChange={handleStateFieldToggle(field)} />}
                  label={label}
                />
              ))}
            </div>
          );

          return (
            <form className={styles.fieldsContainer} onSubmit={formikSubmit}>
              <TextField
                label={t`Name`}
                name="name"
                helperText={errors.name}
                className={styles.fieldStyle}
                variant="standard"
                onChange={handleChange}
                value={values.name || ""}
                error={!!errors.name}
              />

              <Tooltip title={isEdit ? t`Cannot be changed after creation` : ""}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={!!values.simulatorConnected}
                      onChange={handleSimulatorConnectedChange}
                      disabled={isEdit}
                    />
                  )}
                  label={t`Connect to Simulator`}
                />
              </Tooltip>

              <div className={styles.serialRow}>
                <TextField
                  label={t`Serial`}
                  name="serial"
                  helperText={serialError || errors.serial || (values.simulatorConnected ? t`Simulator format: 283B96 + type + 4 hex` : t`Device Serial`)}
                  className={styles.fieldStyle}
                  variant="standard"
                  onChange={handleSerialChange}
                  value={values.serial || ""}
                  error={!!serialError || !!errors.serial || serialFormatInvalid}
                  disabled={isEdit}
                />
                {!isEdit && (
                  <IconButton
                    className={styles.sparkButton}
                    onClick={handleSpark}
                    disabled={generatingSerial}
                    title={t`Generate serial${values.simulatorConnected ? "" : " and PIN"}`}
                  >
                    <AutoAwesome />
                  </IconButton>
                )}
              </div>

              {!values.simulatorConnected && (
                <TextField
                  label={t`PIN`}
                  name="pin"
                  helperText={errors.pin}
                  className={styles.fieldStyle}
                  variant="standard"
                  onChange={handleChange}
                  value={values.pin || ""}
                  error={!!errors.pin}
                  disabled={isEdit}
                />
              )}

              {renderStateCheckboxes()}

              <div className={styles.actionsContainer}>
                {isEdit && (
                  <Button onClick={onRequestDelete} className={clsx(styles.sharedButtonStyle, styles.deleteButton)}>
                    {t`Delete`}
                  </Button>
                )}
                <div className={styles.actionsRight}>
                  <Button onClick={onClose} className={styles.sharedButtonStyle}>
                    {t`Cancel`}
                  </Button>
                  <Button
                    type="submit"
                    disabled={saveDisabled}
                    className={clsx(styles.sharedButtonStyle, styles.submitButton)}
                  >
                    {isEdit ? t`Update` : t`Create`}
                  </Button>
                </div>
              </div>
            </form>
          );
        }}
      </Formik>
    </SimpleModal>
  );
};

export default AddEditMockDevice;
